import { useEffect, useState } from 'react'
import { useContentfulResurrection } from './useContentfulResurrection'
import { ResurrectionContent } from '../../../services/resurrectionContentful'
import { useFlag } from '@unleash/proxy-client-react'
import { FF_RESURRECTION_CONTENTFUL_ENABLED } from 'src/constants/feature-flags'
import { useLocation } from 'react-router-dom'

/**
 * Interface for the actions object that contains API methods
 */
interface Actions {
  getResurrectionCampaign: (params: { campaignSlug: string }) => void
}

/**
 * Interface for the resurrection campaign data
 */
export interface ResurrectionCampaign {
  id?: string
  heroPromotionCopy?: string
  heroText?: string
  heroTitle?: string
  heroSubtitle?: string
  heroImageUrl?: string
  menuSectionTitle?: string
  highlightedMealsIds?: number[]
  conditionsCopy?: string
  couponCode?: string
  browserTitle?: string
  newDesign?: boolean
  [key: string]: any
}

/**
 * Maps Contentful data to the legacy ResurrectionCampaign format
 */
function mapContentfulToLegacy(
  data: ResurrectionContent
): ResurrectionCampaign {
  const mapped = {
    id: data.slug,
    heroPromotionCopy: data.heroTitle, // Use heroTitle as heroPromotionCopy for backwards compatibility
    heroText: data.heroText, // Map heroText from Contentful
    heroTitle: data.heroTitle,
    heroSubtitle: data.heroSubtitle,
    heroImageUrl: data.heroImage,
    heroImageMobileUrl: data.heroImageMobile,
    menuSectionTitle: data.menuSectionTitle,
    highlightedMealsIds: data.highlightedMealsIds,
    conditionsCopy: data.conditionsCopy,
    couponCode: data.couponCode,
    browserTitle: data.browserTitle,
    newDesign: data.newDesign,
    urlParams: data.urlParams
  }
  return mapped
}

/**
 * Custom hook to fetch and manage resurrection campaign data
 *
 * This is a compatibility layer that maintains the same API as before
 * but internally uses the Contentful integration when the feature flag is enabled.
 *
 * @param campaignSlug - The campaign identifier from URL params
 * @param actions - Actions object containing API methods
 * @param isPending - External loading state to track
 * @returns Campaign data and loading state
 */
export function useResurrectionCampaign(
  campaignSlug: string | undefined,
  actions: Actions,
  isPending: boolean
) {
  // Track if we've initiated the campaign fetch
  const [hasFetchedCampaign, setHasFetchedCampaign] = useState(false)
  const [hasTriggeredLegacyFallback, setHasTriggeredLegacyFallback] = useState(
    false
  )
  const [
    contentfulTimeoutId,
    setContentfulTimeoutId
  ] = useState<NodeJS.Timeout | null>(null)
  const isContentfulEnabled = useFlag(FF_RESURRECTION_CONTENTFUL_ENABLED)
  const location = useLocation()

  // For hardcoded routes (like /resurrection-50x4), extract slug from pathname
  // For dynamic routes (like /resurrection/:campaignSlug), use the campaignSlug parameter
  // Handle trailing slashes in the pathname safely
  const effectiveSlug =
    campaignSlug ||
    (() => {
      // Remove '/resurrection' or '/resurrection/' from the start of the pathname
      // Also remove leading dash if it appears after removing /resurrection
      const pathWithoutResurrection = location.pathname
        .replace(/^\/resurrection\/?/, '')
        .replace(/^-/, '')

      // For paths that start with /resurrection- (like /resurrection-landing-35offx6),
      // the Contentful entry likely has the full slug including resurrection-
      if (location.pathname.startsWith('/resurrection-')) {
        return location.pathname.slice(1) // Remove leading slash, keep resurrection- prefix
      }

      return pathWithoutResurrection
    })()

  // Construct the full slug including query parameters for Contentful lookup
  let baseSlug = effectiveSlug
  // Add trailing slash for Chef mkt slugs if not present
  if (baseSlug && /^gem[-_]/.test(baseSlug) && !baseSlug.endsWith('/')) {
    baseSlug += '/'
  }

  // Append any query parameters (e.g., ?chefBrandIds=...) because many Contentful entries
  // keep them as part of the slug. This is a no-op when there is no search string.
  const contentfulSlug = `${baseSlug}${location.search || ''}`

  // Use the new Contentful hook when flag is enabled
  const contentfulResult = useContentfulResurrection(
    isContentfulEnabled ? contentfulSlug : undefined
  )

  // Set up timeout for Contentful requests that take too long
  useEffect(() => {
    if (
      isContentfulEnabled &&
      contentfulResult.isPending &&
      !hasTriggeredLegacyFallback
    ) {
      const timeoutId = setTimeout(() => {
        if (effectiveSlug) {
          actions.getResurrectionCampaign({ campaignSlug: effectiveSlug })
          setHasFetchedCampaign(true)
          setHasTriggeredLegacyFallback(true)
        }
      }, 2000) // 2 second timeout for faster fallback

      setContentfulTimeoutId(timeoutId)

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      }
    }
  }, [
    isContentfulEnabled,
    contentfulResult.isPending,
    hasTriggeredLegacyFallback,
    effectiveSlug,
    actions
  ])

  // Clear timeout when Contentful resolves
  useEffect(() => {
    if (
      contentfulTimeoutId &&
      (!contentfulResult.isPending || contentfulResult.data)
    ) {
      clearTimeout(contentfulTimeoutId)
      setContentfulTimeoutId(null)
    }
  }, [contentfulResult.isPending, contentfulResult.data, contentfulTimeoutId])

  // Legacy API call when Contentful is disabled OR as fallback when Contentful fails
  useEffect(() => {
    const shouldTriggerLegacyFetch =
      !isContentfulEnabled ||
      (isContentfulEnabled &&
        !contentfulResult.isPending &&
        !contentfulResult.data &&
        !hasTriggeredLegacyFallback) ||
      (isContentfulEnabled &&
        contentfulResult.isError &&
        !hasTriggeredLegacyFallback)

    if (shouldTriggerLegacyFetch && effectiveSlug) {
      actions.getResurrectionCampaign({ campaignSlug: effectiveSlug })
      setHasFetchedCampaign(true)

      if (
        isContentfulEnabled &&
        (!contentfulResult.data || contentfulResult.isError)
      ) {
        setHasTriggeredLegacyFallback(true)
      }
    }
  }, [
    effectiveSlug,
    actions,
    hasFetchedCampaign,
    isContentfulEnabled,
    contentfulResult.isPending,
    contentfulResult.data,
    contentfulResult.isError,
    hasTriggeredLegacyFallback
  ])

  const result = {
    hasFetchedCampaign: isContentfulEnabled
      ? !contentfulResult.isPending &&
        (!!contentfulResult.data || hasTriggeredLegacyFallback)
      : hasFetchedCampaign,
    isPending: isContentfulEnabled
      ? contentfulResult.isPending && !hasTriggeredLegacyFallback
      : isPending,
    contentfulData: contentfulResult.data
      ? mapContentfulToLegacy(contentfulResult.data)
      : undefined,
    isContentfulEnabled
  }

  return {
    result,
    isLoading: isContentfulEnabled
      ? contentfulResult.isPending && !hasTriggeredLegacyFallback
      : isPending
  }
}
